import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import axios from "axios"
import { differenceInDays } from "date-fns"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Card, CardDescription, CardHeader, CardTitle } from "design-system/molecules/Card"
import { useDeleteWhatsappTemplateMutation } from "modules/campaigns/hooks/sections/whatsapp/useDeleteWhatsappTemplateMutation"
import { useUpdateWhatsappTemplateMutation } from "modules/campaigns/hooks/sections/whatsapp/useUpdateWhatsappTemplateMutation"
import { useWhatsappTemplateQuery } from "modules/campaigns/hooks/sections/whatsapp/useWhatsappTemplateQuery"
import { WhatsappTemplateFormSchema } from "modules/campaigns/lib/whatsappTemplateFormSchema"
import { HeaderButtons } from "modules/shared/components/Breadcrumbs/HeaderButtons"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { SearchSchema } from "routes/_base/_auth/settings/channels/whatsapp/templates"
import { Route as WhatsappTemplateDetailRoute } from "routes/_base/_auth/settings/channels/whatsapp/templates/$id"
import { CreateWhatsappTemplateSchema, Errors, UpdateWhatsappTemplateBodySchema } from "schema"
import { toast } from "sonner"
import { z } from "zod"
import { WhatsappMessageCreator } from "./WhatsappMessageCreator"
import { WhatsappTemplateTranslate } from "./WhatsappTemplateTranslate"

export const WhatsappTemplateDetail = () => {
  const navigate = useNavigate()
  const { id } = WhatsappTemplateDetailRoute.useParams()
  const { mutateAsync: deleteTemplate, isSuccess, isPending } = useDeleteWhatsappTemplateMutation()
  const { mutateAsync: updateTemplate, isPending: isUpdateTemplatePending } =
    useUpdateWhatsappTemplateMutation()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const { t } = useTranslation()

  const form = useForm<z.infer<typeof CreateWhatsappTemplateSchema>>({
    resolver: zodResolver(WhatsappTemplateFormSchema),
    // Errors are triggered on load, so they must also clear while editing before the first submit.
    mode: "onChange",
    defaultValues: {
      template: {
        name: "",
        language: "",
        header: "",
        body: "",
        footer: "",
        buttons: [],
        whatsappTemplateVariables: [],
      },
    },
  })

  const { data } = useWhatsappTemplateQuery({
    id,
    disabled: isSuccess || isPending,
  })

  const templateIsPending = data && data.status === "PENDING"
  const templateLastEdit24h =
    data &&
    data.status === "APPROVED" &&
    data.editedAt &&
    differenceInDays(new Date(), data.editedAt) < 1

  function onDelete() {
    deleteTemplate({ id }).then(() => {
      navigate({
        to: "/settings/channels/whatsapp/templates",
        search: SearchSchema.parse({}),
      })
      toast.success("Template deleted successfully")
    })
  }

  function onOpenDeleteDialog(open = true) {
    if (isPending) return

    setIsDeleteDialogOpen(open)
  }

  async function onSubmit(data: z.infer<typeof UpdateWhatsappTemplateBodySchema>) {
    try {
      await updateTemplate({ id, props: data })
      navigate({ to: "/settings/channels/whatsapp/templates", search: SearchSchema.parse({}) })
      toast.success(t("campaigns.templates.whatsapp.success.whatsapp.edit"))
    } catch (error) {
      if (
        axios.isAxiosError<{ error?: { key?: string } }>(error) &&
        error.response?.data.error?.key === Errors.WHATSAPP_TEMPLATE_VARIABLE_AT_EDGE
      ) {
        form.setError("template.body", {
          type: "server",
          message: Errors.WHATSAPP_TEMPLATE_VARIABLE_AT_EDGE,
        })
        return
      }

      throw error
    }
  }

  useEffect(() => {
    if (data) {
      form.reset({
        template: {
          name: data.name,
          category: data.category,
          language: data.language,
          header: data.header ?? "",
          body: data.body,
          footer: data.footer ?? "",
          buttons: (data.whatsappTemplateButtons ?? []).map((button) => ({
            text: button.text,
            urlType: button.urlType,
            url: button.url ?? undefined,
            variableName: button.variableName ?? undefined,
            variableDescription: button.variableDescription ?? undefined,
          })),
          whatsappTemplateVariables: data.whatsappTemplateVariables ?? [],
        },
        media: {
          format: data.mediaFormat,
          url: data.mediaUrl ?? "",
        },
      })
      form.trigger()
    }
  }, [data, form])

  return (
    <>
      {!isPending && data && (
        <HeaderButtons>
          <WhatsappTemplateTranslate {...data} />
        </HeaderButtons>
      )}
      {templateIsPending && (
        <Card className="bg-warning/5 border-warning/50 text-warning">
          <CardHeader>
            <CardTitle>{t("campaigns.templates.whatsapp.detail.pendingStatus.title")}</CardTitle>
            <CardDescription>
              {t("campaigns.templates.whatsapp.detail.pendingStatus.description")}
            </CardDescription>
          </CardHeader>
        </Card>
      )}
      {templateLastEdit24h && (
        <Card className="bg-warning/5 border-warning/50 text-warning">
          <CardHeader>
            <CardTitle>{t("campaigns.templates.whatsapp.detail.editedLast24h.title")}</CardTitle>
            <CardDescription>
              {t("campaigns.templates.whatsapp.detail.editedLast24h.description")}
            </CardDescription>
          </CardHeader>
        </Card>
      )}
      <WhatsappMessageCreator
        form={form}
        onDelete={onOpenDeleteDialog}
        mode={(data && data.status === "PENDING") || templateLastEdit24h ? "view" : "edit"}
        onSubmit={onSubmit}
        isPending={isUpdateTemplatePending}
      />
      <Dialog open={isDeleteDialogOpen} onOpenChange={onOpenDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("campaigns.templates.whatsapp.detail.config.actions.delete.title")}
            </DialogTitle>
          </DialogHeader>
          <DialogDescription>
            {t("campaigns.templates.whatsapp.detail.config.actions.delete.description")}
          </DialogDescription>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isPending}
            >
              {t("shared.cancel")}
            </Button>
            <Button variant="destructive" onClick={onDelete} disabled={isPending}>
              {t("campaigns.templates.whatsapp.detail.config.actions.delete.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
